// Página AdminCustomers - Gestión de clientes
// Listado de clientes extraídos desde los pedidos con estadísticas

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getAllCustomers } from '../../services/customers.service'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import './AdminCustomers.css'

/**
 * Página AdminCustomers
 * Gestión de clientes para administradores
 */
function AdminCustomers() {
  const [customers, setCustomers] = useState([])
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('lastOrderDate')
  const [sortDirection, setSortDirection] = useState('desc')

  // Estadísticas generales
  const [stats, setStats] = useState({
    total: 0,
    totalSpent: 0,
    averageOrderValue: 0
  })

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const customersData = await getAllCustomers()
      setCustomers(customersData)
      
      // Calcular estadísticas
      calculateStats(customersData)
    } catch (err) {
      console.error('Error cargando clientes:', err)
      setError('Error al cargar los clientes')
    } finally {
      setLoading(false)
    }
  }, [])

  const calculateStats = (customersData) => {
    const total = customersData.length
    const totalSpent = customersData.reduce((sum, customer) => sum + customer.totalSpent, 0)
    const totalOrders = customersData.reduce((sum, customer) => sum + customer.totalOrders, 0)
    const averageOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0

    setStats({
      total,
      totalSpent,
      averageOrderValue
    })
  }

  const applyFilters = useCallback(() => {
    let filtered = [...customers]

    // Filtrar por búsqueda (nombre, email, teléfono)
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(customer => {
        const name = (customer.name || '').toLowerCase()
        const email = (customer.email || '').toLowerCase()
        const phone = (customer.phone || '').toLowerCase()
        return name.includes(search) || email.includes(search) || phone.includes(search)
      })
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortBy]
      let bValue = b[sortBy]

      // Manejar fechas
      if (sortBy === 'lastOrderDate' || sortBy === 'firstOrderDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0
        bValue = bValue ? new Date(bValue).getTime() : 0
      }

      // Manejar números
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      // Manejar strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase()
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      }
    })

    setFilteredCustomers(filtered)
  }, [customers, searchTerm, sortBy, sortDirection])

  // Cargar clientes
  useEffect(() => {
    fetchCustomers()
  }, [fetchCustomers])

  // Aplicar filtros y ordenamiento
  useEffect(() => {
    applyFilters()
  }, [applyFilters])

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Se podría agregar un toast aquí
      console.log('Copiado al portapapeles:', text)
    })
  }

  if (loading) {
    return (
      <div className="admin-customers-page">
        <div className="loading-state">
          <p>Cargando clientes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="admin-customers-page">
        <div className="error-state">
          <p>{error}</p>
          <Button onClick={fetchCustomers}>Reintentar</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-customers-page">
      {/* Header */}
      <div className="customers-header">
        <div>
          <h1>Gestión de Clientes</h1>
          <p className="customers-subtitle">
            Administra y visualiza información de todos los clientes
          </p>
        </div>
        <Button onClick={fetchCustomers} variant="outline">
          🔄 Actualizar
        </Button>
      </div>

      {/* Estadísticas */}
      <div className="customers-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total de Clientes</div>
        </div>
        <div className="stat-card stat-revenue">
          <div className="stat-value">{formatCurrency(stats.totalSpent)}</div>
          <div className="stat-label">Total Facturado</div>
        </div>
        <div className="stat-card stat-average">
          <div className="stat-value">{formatCurrency(Math.round(stats.averageOrderValue))}</div>
          <div className="stat-label">Ticket Promedio</div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="customers-filters">
        <div className="filter-group">
          <label htmlFor="searchTerm">Buscar:</label>
          <input
            type="text"
            id="searchTerm"
            placeholder="Nombre, email, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="sortBy">Ordenar por:</label>
          <select
            id="sortBy"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="lastOrderDate">Último Pedido</option>
            <option value="firstOrderDate">Primer Pedido</option>
            <option value="totalOrders">Total de Pedidos</option>
            <option value="totalSpent">Total Gastado</option>
            <option value="name">Nombre</option>
            <option value="email">Email</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="sortDirection">Dirección:</label>
          <select
            id="sortDirection"
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value)}
          >
            <option value="desc">Descendente</option>
            <option value="asc">Ascendente</option>
          </select>
        </div>
      </div>

      {/* Lista de clientes */}
      <div className="customers-list">
        {filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <p>No se encontraron clientes con los filtros aplicados</p>
          </div>
        ) : (
          <div className="customers-grid">
            {filteredCustomers.map((customer) => {
              const lastOrderDate = customer.lastOrderDate
                ? formatDate(customer.lastOrderDate)
                : 'N/A'
              
              const firstOrderDate = customer.firstOrderDate
                ? formatDate(customer.firstOrderDate)
                : 'N/A'

              return (
                <div key={customer.email} className="customer-card">
                  <div className="customer-card-header">
                    <div className="customer-avatar">
                      {customer.name?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div className="customer-info">
                      <h3 className="customer-name">{customer.name}</h3>
                      <p className="customer-email">{customer.email}</p>
                    </div>
                  </div>

                  <div className="customer-card-body">
                    <div className="customer-detail-row">
                      <span className="detail-label">Teléfono:</span>
                      <span className="detail-value">
                        {customer.phone || 'N/A'}
                        {customer.phone && (
                          <button
                            className="copy-btn"
                            onClick={() => copyToClipboard(customer.phone)}
                            title="Copiar teléfono"
                          >
                            📋
                          </button>
                        )}
                      </span>
                    </div>

                    <div className="customer-detail-row">
                      <span className="detail-label">Total de Pedidos:</span>
                      <span className="detail-value highlight">{customer.totalOrders}</span>
                    </div>

                    <div className="customer-detail-row">
                      <span className="detail-label">Total Gastado:</span>
                      <span className="detail-value highlight revenue">
                        {formatCurrency(customer.totalSpent)}
                      </span>
                    </div>

                    <div className="customer-detail-row">
                      <span className="detail-label">Primer Pedido:</span>
                      <span className="detail-value">{firstOrderDate}</span>
                    </div>

                    <div className="customer-detail-row">
                      <span className="detail-label">Último Pedido:</span>
                      <span className="detail-value">{lastOrderDate}</span>
                    </div>

                    {customer.addresses.length > 0 && (
                      <div className="customer-addresses">
                        <span className="detail-label">Direcciones:</span>
                        <div className="addresses-list">
                          {customer.addresses.slice(0, 2).map((addr, idx) => (
                            <p key={idx} className="address-item">{addr.full}</p>
                          ))}
                          {customer.addresses.length > 2 && (
                            <p className="address-more">
                              +{customer.addresses.length - 2} dirección(es) más
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="customer-card-footer">
                    <Link to={`/admin/clientes/${encodeURIComponent(customer.email)}`}>
                      <Button variant="primary" size="small">
                        Ver Detalle
                      </Button>
                    </Link>
                    <button
                      className="action-btn"
                      onClick={() => copyToClipboard(customer.email)}
                      title="Copiar email"
                    >
                      📧
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminCustomers

